import React, { useEffect, useState } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ArrowUpRight, ArrowDownRight, Minus, RefreshCw, AlertCircle } from "lucide-react";
import { adminApi } from "../../../lib/api";
import NotConnected from "./NotConnected";
import AdsCredentialsForm from "./AdsCredentialsForm";

const PERIODS = [7, 28, 90, 365];

const HEADLINE = [
  ["cost", "Spend"],
  ["clicks", "Clicks"],
  ["impressions", "Impressions"],
  ["conversions", "Conversions"],
  ["cost_per_conversion", "Cost per conversion"],
  ["cpc", "Cost per click"],
  ["ctr", "Click-through rate"],
  ["conversion_rate", "Conversion rate"],
];

const money = (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt = (key, v) => {
  if (v === null || v === undefined) return "—";
  if (key === "ctr" || key === "conversion_rate") return `${(v * 100).toFixed(2)}%`;
  if (["cost", "cpc", "cost_per_conversion"].includes(key)) return money(v);
  if (key === "conversions") return Number(v).toFixed(v % 1 ? 2 : 0);
  return Math.round(v).toLocaleString();
};

// Spend rising is not automatically good, so cost movements are shown neutrally
// and only the outcome measures are coloured.
const TONE = { cost: "neutral", impressions: "neutral", cpc: "inverse", cost_per_conversion: "inverse" };

const Trend = ({ k, m }) => {
  if (!m || m.pct === null || m.pct === undefined) return <span className="text-[11px] text-slate-400">no comparison</span>;
  const Icon = m.direction === "up" ? ArrowUpRight : m.direction === "down" ? ArrowDownRight : Minus;
  const mode = TONE[k] || "normal";
  let tone = "text-slate-400";
  if (mode === "normal") tone = m.direction === "up" ? "text-emerald-600" : m.direction === "down" ? "text-red-500" : "text-slate-400";
  if (mode === "inverse") tone = m.direction === "down" ? "text-emerald-600" : m.direction === "up" ? "text-red-500" : "text-slate-400";
  return (
    <span className={`text-[11px] font-semibold inline-flex items-center gap-0.5 ${tone}`}>
      <Icon size={12} /> {Math.abs(m.pct)}%
    </span>
  );
};

const Table = ({ title, rows, columns, fields, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid={testid}>
    <p className="text-xs font-bold text-navy px-4 pt-4">{title}</p>
    <table className="w-full text-sm mt-2 min-w-[480px]">
      <thead>
        <tr className="text-left text-slate-400 border-b border-slate-100">
          <th className="py-2 px-4 font-semibold">{columns[0]}</th>
          {columns.slice(1).map((c) => <th key={c} className="py-2 px-4 font-semibold text-right">{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {(rows || []).slice(0, 15).map((r, i) => (
          <tr key={`${r.key}-${i}`} className="border-b border-slate-50">
            <td className="py-2 px-4 text-navy [overflow-wrap:anywhere]">{r.key || "(not set)"}</td>
            {fields.map((f) => (
              <td key={f} className="py-2 px-4 text-right text-slate-500 tabular-nums">
                {typeof r[f] === "string" ? r[f] : fmt(f, r[f])}
              </td>
            ))}
          </tr>
        ))}
        {(rows || []).length === 0 && (
          <tr><td className="py-3 px-4 text-xs text-slate-400" colSpan={columns.length}>Nothing in this period.</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

export default function GoogleAdsTab() {
  const [days, setDays] = useState(28);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (d = days) => {
    setLoading(true);
    adminApi.googleAds.status().then(setStatus).catch(() => setStatus(null));
    adminApi.googleAds
      .report(d)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  if (loading && !data) return <p className="text-sm text-slate-500">Loading…</p>;
  if (!data) return <p className="text-sm text-red-500">Google Ads reporting could not be loaded.</p>;

  if (!data.connected) {
    return (
      <div className="space-y-4" data-testid="ads-tab-setup">
        <NotConnected
          title="Google Ads is not connected"
          reason="Spend, clicks and cost per conversion cannot be reported until the account is linked. The connection is read-only — campaigns, budgets, bidding, keywords and conversion actions are never written to."
          needs={data.needs || []}
          links={[
            { href: "https://ads.google.com/aw/overview", label: "Open Google Ads" },
            { href: "https://console.cloud.google.com/apis/credentials", label: "OAuth credentials" },
            { href: "https://ads.google.com/aw/apicenter", label: "API Center" },
          ]}
          testid="ads-tab-not-connected"
        />
        <AdsCredentialsForm status={status} onChange={() => load(days)} />
      </div>
    );
  }

  const s = data.summary || {};

  return (
    <div className="space-y-6" data-testid="ads-report">
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            data-testid={`ads-period-${d}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-bold duration-200 [transition-property:background-color,color] ${
              days === d ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {d === 365 ? "12 months" : `${d} days`}
          </button>
        ))}
        <button
          type="button"
          onClick={() => load(days)}
          data-testid="ads-refresh"
          className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-navy duration-200 [transition-property:color]"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {data.error ? (
        <div className="bg-white rounded-xl border border-red-200 p-5 flex items-start gap-3" data-testid="ads-error">
          <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-navy text-sm">Google returned an error</p>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">{data.error}</p>
            <p className="text-[11px] text-slate-400 mt-2">Account {data.customer_id}</p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {HEADLINE.map(([k, label]) => (
              <div key={k} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`ads-metric-${k}`}>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
                <p className="text-2xl font-extrabold text-navy mt-1 tabular-nums">{fmt(k, s[k]?.value)}</p>
                <Trend k={k} m={s[k]} />
              </div>
            ))}
          </div>

          {(data.series || []).length > 1 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 h-64" data-testid="ads-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.series} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
                  <defs>
                    <linearGradient id="adsCost" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f97316" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="key" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} minTickGap={24} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                  <Area type="monotone" dataKey="cost" stroke="#f97316" strokeWidth={2} fill="url(#adsCost)" name="Spend (£)" />
                  <Area type="monotone" dataKey="clicks" stroke="#94a3b8" strokeWidth={1} fill="transparent" name="Clicks" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          <Table
            title="Campaigns"
            rows={data.campaigns}
            columns={["Campaign", "Status", "Spend", "Clicks", "Impressions", "Conversions", "Cost / conv."]}
            fields={["status", "cost", "clicks", "impressions", "conversions", "cost_per_conversion"]}
            testid="ads-campaigns"
          />

          <div className="grid lg:grid-cols-2 gap-4">
            <Table
              title="Conversion actions"
              rows={data.conversion_actions}
              columns={["Action", "Conversions"]}
              fields={["conversions"]}
              testid="ads-conversion-actions"
            />
            <Table
              title="Keywords by spend"
              rows={data.keywords}
              columns={["Keyword", "Spend", "Clicks", "Conversions"]}
              fields={["cost", "clicks", "conversions"]}
              testid="ads-keywords"
            />
            <div className="lg:col-span-2">
              <Table
                title="What people actually searched"
                rows={data.search_terms}
                columns={["Search term", "Clicks", "Impressions", "Conversions", "Spend"]}
                fields={["clicks", "impressions", "conversions", "cost"]}
                testid="ads-search-terms"
              />
            </div>
          </div>
        </>
      )}

      <p className="text-[11px] text-slate-400 leading-relaxed">
        Account {data.customer_id} · {data.period?.start} to {data.period?.end} · compared with{" "}
        {data.previous_period?.start} to {data.previous_period?.end} · last read{" "}
        {data.fetched_at ? new Date(data.fetched_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—"}
        {data.from_cache ? " (cached)" : ""}. {data.note}
      </p>

      {status?.source === "saved" && (
        <details data-testid="ads-credentials-manage">
          <summary className="cursor-pointer text-[11px] font-semibold text-slate-400 hover:text-navy duration-200 [transition-property:color]">
            Connection details
          </summary>
          <div className="mt-3">
            <AdsCredentialsForm status={status} onChange={() => load(days)} />
          </div>
        </details>
      )}
    </div>
  );
}
